import { ReactNode } from 'react'
import styled from 'styled-components'

import { Card, FlexBox } from '../../../components/Base/Base'
import { Text } from '../../../components/Text/Text'
import { formatShortDate } from '../../../helpers/formatters'
import { Handler, HandlerOf } from '../../../helpers/typeHelper'
import { usePerformanceScore } from '../../../hooks/query/usePerformaceScore'
import { usePerformanceScoreRanges } from '../../../hooks/query/usePerformanceScoreRanges'
import { useSafetyScore } from '../../../hooks/query/useSafetyScore'
import { useSafetyScoreRanges } from '../../../hooks/query/useSafetyScoreRanges'
import { User } from '../../../models/user'
import { cyanD1, lightGray1, midGray, primaryBlack } from '../../../theme/colors'
import { SafetyInfoTooltip } from './SafetyInfoTooltip'
import { SafetyScoreHeaderItem } from './SafetyScoreHeaderItem'
import { ScorePill } from './ScorePill'

interface SafetyScoreCardsProps {
  user?: User
  isHeader?: boolean
  isInsideSidebar?: boolean
  selectedCard: SafetyScoreCardsType
  setSelectedCard: HandlerOf<SafetyScoreCardsType>
}

export enum SafetyScoreCardsType {
  SAFETY_SCORE = 'SAFETY_SCORE',
  ON_TIME_SERVICE_RATE = 'ON_TIME_SERVICE_RATE',
  LATE_CANCEL_RATE = 'LATE_CANCEL_RATE',
}

export function SafetyScoreCards({ user, selectedCard, setSelectedCard }: SafetyScoreCardsProps) {
  const { safetyScore } = useSafetyScore(user?.id ?? '', { enabled: !!user?.id })
  const { performanceScore } = usePerformanceScore(user?.id ?? '', { enabled: !!user?.id })
  // TODO: Implement late cancellation rate hook once the endpoint is ready
  const { safetyScoreRanges } = useSafetyScoreRanges()
  const { performanceScoreRanges } = usePerformanceScoreRanges()
  const onTimeServiceScore = performanceScore?.find((score) => score.eventType === 'On Time Service')
  const onTimePickupScore = performanceScore?.find((score) => score.eventType === 'On Time Pickup')
  const onTimeDeliveryScore = performanceScore?.find((score) => score.eventType === 'On Time Delivery')

  return (
    <FlexBox isColumn gap="24px" mb={5}>
      <FlexBox gap="20px" mb={2}>
        <SafetyScoreCard
          title="SAFETY SCORE"
          tooltip={
            <SafetyInfoTooltip
              title="Safety scores are based on a driver’s lifecycle and may improve as a user continues to drive."
              ranges={safetyScoreRanges}
            />
          }
          score={{
            score: safetyScore?.safetyScore,
            color: safetyScore?.safetyScoreColor,
            backgroundColor: safetyScore?.safetyScoreBackgroundColor,
          }}
          label={{
            text: safetyScore?.safetyScoreLabel,
            color: safetyScore?.safetyScoreColor,
          }}
          onClick={() => setSelectedCard(SafetyScoreCardsType.SAFETY_SCORE)}
          isSelected={selectedCard === SafetyScoreCardsType.SAFETY_SCORE}
          lastUpdated={safetyScore?.runTs}
        />
        <SafetyScoreCard
          title="ON-TIME SERVICE RATE"
          tooltip={
            <SafetyInfoTooltip
              title="On-time rates are updated daily and reflect data from your most recent 20 jobs in the past 12 months."
              ranges={performanceScoreRanges}
            />
          }
          score={{
            score: onTimeServiceScore?.score,
            color: onTimeServiceScore?.scoreColor,
            backgroundColor: onTimeServiceScore?.scoreBackgroundColor,
            isPercentage: true,
          }}
          label={{
            text: onTimeServiceScore?.scoreLabel,
            color: onTimeServiceScore?.scoreColor,
          }}
          onClick={() => setSelectedCard(SafetyScoreCardsType.ON_TIME_SERVICE_RATE)}
          isSelected={selectedCard === SafetyScoreCardsType.ON_TIME_SERVICE_RATE}
          lastUpdated={onTimeServiceScore?.dateUpdated}
        />
      </FlexBox>

      <HeaderContainer>
        {selectedCard === SafetyScoreCardsType.SAFETY_SCORE && (
          <FlexBox isColumn gap="17px" ml={-1}>
            <SafetyScoreHeaderItemContainer zeroth>
              <SafetyScoreHeaderItem
                title="Driving behaviors"
                color={safetyScore?.drivingBehaviorColor}
                label={safetyScore?.drivingBehaviorLabel}
                score={safetyScore?.drivingBehaviorScore}
              />
            </SafetyScoreHeaderItemContainer>

            <SafetyScoreHeaderItemContainer>
              <SafetyScoreHeaderItem
                title="Accidents & violations"
                color={safetyScore?.accidentsViolationsColor}
                label={safetyScore?.accidentsViolationsLabel}
                score={safetyScore?.accidentsViolationsScore}
              />
            </SafetyScoreHeaderItemContainer>

            <SafetyScoreHeaderItemContainer>
              <SafetyScoreHeaderItem
                title="Compliance"
                color={safetyScore?.onRoadComplianceColor}
                label={safetyScore?.onRoadComplianceLabel}
                score={safetyScore?.onRoadComplianceScore}
              />
            </SafetyScoreHeaderItemContainer>
          </FlexBox>
        )}

        {selectedCard === SafetyScoreCardsType.ON_TIME_SERVICE_RATE && (
          <FlexBox isColumn gap="17px" ml={36.5}>
            <SafetyScoreHeaderItemContainer zeroth>
              <SafetyScoreHeaderItem
                title="On-time pickup"
                color={onTimePickupScore?.scoreColor}
                label={onTimePickupScore?.scoreLabel}
                score={onTimePickupScore?.score}
                isPercentage
              />
            </SafetyScoreHeaderItemContainer>

            <SafetyScoreHeaderItemContainer>
              <SafetyScoreHeaderItem
                title="On-time delivery"
                color={onTimeDeliveryScore?.scoreColor}
                label={onTimeDeliveryScore?.scoreLabel}
                score={onTimeDeliveryScore?.score}
                isPercentage
              />
            </SafetyScoreHeaderItemContainer>
          </FlexBox>
        )}
      </HeaderContainer>
    </FlexBox>
  )
}

const HeaderContainer = styled(FlexBox)`
  height: 120px;
`

function SafetyScoreHeaderItemContainer({ children, zeroth }: { children: ReactNode; zeroth?: boolean }) {
  return (
    <FlexBox gap="32px" styles="position: relative;">
      <ArrowShape $zeroth={zeroth} />
      <FlexBox ml={5} isFullWidth>
        {children}
      </FlexBox>
    </FlexBox>
  )
}

const ArrowShape = styled.div<{ $zeroth?: boolean }>`
  position: absolute;
  width: 22px;
  height: ${({ $zeroth }) => 30 + ($zeroth ? 0 : 8)}px;
  border-left: 2px solid ${lightGray1};
  border-bottom: 2px solid ${lightGray1};
  top: ${({ $zeroth }) => -20 - ($zeroth ? 0 : 8)}px;
  left: 8px;
`

const SafetyScoreCard = ({
  title,
  tooltip,
  score,
  label,
  lastUpdated,
  onClick,
  isSelected,
}: {
  title: string
  tooltip: ReactNode
  score: {
    score?: number | null
    color?: string | null
    backgroundColor?: string | null
    isPercentage?: boolean | null
  }
  label: {
    text?: string | null
    color?: string | null
  }
  lastUpdated?: string | null
  onClick: Handler
  isSelected: boolean
}) => {
  return (
    <ScoreCard onClick={onClick} isSelected={isSelected}>
      <FlexBox isColumn alignItems="flex-start" gap="12px">
        <FlexBox isFullWidth justifyContent="space-between" alignItems="center">
          <Text variant="uppercaseLabel3" mt={0.5}>
            {title}
          </Text>
          {tooltip}
        </FlexBox>
        <FlexBox gap="8px">
          <ScorePill
            score={score.score}
            color={score.color}
            backgroundColor={score.backgroundColor}
            isPercentage={score.isPercentage}
            size="medium"
          />
          <Text variant="header3" fontColor={primaryBlack} mt={0.7}>
            {label?.text}
          </Text>
        </FlexBox>
        <Text variant="label3" fontColor={midGray} mt={0.5}>
          Updated on {lastUpdated && formatShortDate(lastUpdated)}
        </Text>
      </FlexBox>
    </ScoreCard>
  )
}

const ScoreCard = styled(Card)<{ isSelected: boolean }>`
  border: 1px solid ${({ isSelected }) => (isSelected ? cyanD1 : 'transparent')};
  padding: 20px 18px;
  cursor: pointer;
`
